import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaHistory, FaUsers, FaFileAlt, FaBook, FaChevronRight } from 'react-icons/fa';
import useStrapi from '../../hooks/useStrapi';
import jsPDF from 'jspdf';
import { processLinksForNewTab } from '../../utils/htmlUtils';

const PopupOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const PopupContainer = styled(motion.div)`
  background: white;
  border-radius: 15px;
  width: 90%;
  max-width: 1000px;
  height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const PopupHeader = styled.div`
  padding: 20px 30px;
  background: linear-gradient(135deg, #0066cc, #004999);
  color: white;
  position: relative;
  display: flex;
  align-items: center;
`;

const PopupTitle = styled.h2`
  font-size: 1.8rem;
  margin: 0;
  flex: 1;
  color: white;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: rotate(90deg);
  }
`;

const PopupContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  width: 280px;
  background: #f5f7fa;
  border-right: 1px solid #e1e5eb;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    max-width: 100vw;
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    border-right: none;
    border-bottom: 1px solid #e1e5eb;
    background: #f5f7fa;
    padding: 0 0 2px 0;
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 15px 20px;
  text-align: left;
  background: ${props => props.$active ? 'white' : 'transparent'};
  border: none;
  border-left: 4px solid ${props => props.$active ? '#0066cc' : 'transparent'};
  color: ${props => props.$active ? '#0066cc' : '#667188'};
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$active ? 'white' : '#edf0f5'};
    color: #0066cc;
  }

  .icon-container {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .arrow {
    opacity: ${props => props.$active ? '1' : '0'};
    transform: ${props => props.$active ? 'translateX(0)' : 'translateX(-10px)'};
    transition: all 0.2s ease;
  }

  @media (max-width: 768px) {
    width: auto;
    min-width: 120px;
    padding: 12px 10px;
    border-left: none;
    border-bottom: 3px solid ${props => props.$active ? '#0066cc' : 'transparent'};
    border-radius: 0;
    justify-content: center;
    .arrow { display: none; }
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 30px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 18px 8px 18px 8px;
  }
`;

const TabContent = styled(motion.div)`
  max-width: 700px;
  margin: 0 auto;
`;

const AboutPopupSidebar = ({ isOpen, onClose }) => {
  const [activeSection, setActiveSection] = useState('historique');
  
  // Fonction pour convertir le contenu HTML en PDF et le télécharger
  const downloadAsPDF = async (content, filename, title) => {
    try {
      // Créer le PDF directement avec jsPDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Configuration de la page
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let currentY = margin;
      
      // Fonction pour ajouter une nouvelle page si nécessaire
      const checkNewPage = (neededHeight = 10) => {
        if (currentY + neededHeight > pageHeight - margin) {
          pdf.addPage();
          currentY = margin;
          return true;
        }
        return false;
      };
      
      // Fonction pour analyser et appliquer les styles CSS d'un élément
      const getElementStyles = (element) => {
        const styles = window.getComputedStyle(element);
        return {
          fontSize: parseInt(styles.fontSize) || 11,
          fontWeight: styles.fontWeight,
          fontStyle: styles.fontStyle,
          color: styles.color,
          textAlign: styles.textAlign || 'left',
          marginTop: parseInt(styles.marginTop) || 0,
          marginBottom: parseInt(styles.marginBottom) || 0,
          lineHeight: parseFloat(styles.lineHeight) || 1.2
        };
      };
      
      // Fonction pour convertir une couleur CSS en RGB
      const parseColor = (colorStr) => {
        if (!colorStr || colorStr === 'rgb(51, 51, 51)') return [51, 51, 51]; // Couleur par défaut
        
        // Gérer les couleurs rgb()
        const rgbMatch = colorStr.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
          return [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])];
        }
        
        // Gérer les couleurs hex
        if (colorStr.startsWith('#')) {
          const hex = colorStr.slice(1);
          const r = parseInt(hex.substr(0, 2), 16);
          const g = parseInt(hex.substr(2, 2), 16);
          const b = parseInt(hex.substr(4, 2), 16);
          return [r, g, b];
        }
        
        // Couleurs nommées communes
        const namedColors = {
          'black': [0, 0, 0],
          'white': [255, 255, 255],
          'red': [255, 0, 0],
          'blue': [0, 0, 255],
          'green': [0, 128, 0]
        };
        
        return namedColors[colorStr.toLowerCase()] || [51, 51, 51];
      };
      
      // Fonction pour appliquer les styles au PDF
      const applyStyles = (element, defaultSize = 11) => {
        const styles = getElementStyles(element);
        const tagName = element.tagName?.toLowerCase();
        
        // Déterminer la taille de police
        let fontSize = defaultSize;
        if (tagName === 'h1') fontSize = 18;
        else if (tagName === 'h2') fontSize = 16;
        else if (tagName === 'h3') fontSize = 14;
        else if (tagName === 'h4') fontSize = 12;
        else if (tagName === 'h5') fontSize = 11;
        else if (tagName === 'h6') fontSize = 10;
        else if (styles.fontSize > 8) fontSize = Math.min(styles.fontSize * 0.75, 20); // Conversion px vers pt approximative
        
        pdf.setFontSize(fontSize);
        
        // Déterminer le style de police
        const isBold = styles.fontWeight === 'bold' || styles.fontWeight === '700' || styles.fontWeight === '600' || 
                      ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'b'].includes(tagName);
        const isItalic = styles.fontStyle === 'italic' || tagName === 'em' || tagName === 'i';
        
        let fontStyle = 'normal';
        if (isBold && isItalic) fontStyle = 'bolditalic';
        else if (isBold) fontStyle = 'bold';
        else if (isItalic) fontStyle = 'italic';
        
        pdf.setFont('helvetica', fontStyle);
        
        // Appliquer la couleur
        const [r, g, b] = parseColor(styles.color);
        pdf.setTextColor(r, g, b);
        
        return {
          fontSize,
          isBold,
          isItalic,
          color: [r, g, b],
          align: styles.textAlign,
          lineHeight: styles.lineHeight
        };
      };
      
      // En-tête du document
      pdf.setFontSize(10);
      pdf.setTextColor(102, 102, 102);
      pdf.setFont('helvetica', 'normal');
      const headerText = `Document généré le ${new Date().toLocaleDateString('fr-FR')} - Office des Sports de l'Agglomération de la Région de Compiègne (OSARC)`;
      const headerLines = pdf.splitTextToSize(headerText, contentWidth);
      pdf.text(headerLines, margin, currentY);
      currentY += headerLines.length * 5 + 15;
      
      // Titre principal
      pdf.setFontSize(18);
      pdf.setTextColor(0, 102, 204);
      pdf.setFont('helvetica', 'bold');
      const titleLines = pdf.splitTextToSize(title, contentWidth);
      titleLines.forEach(line => {
        checkNewPage(12);
        pdf.text(line, pageWidth / 2, currentY, { align: 'center' });
        currentY += 8;
      });
      currentY += 15;
      
      // Ligne de séparation
      pdf.setDrawColor(0, 102, 204);
      pdf.setLineWidth(0.5);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 15;
      
      // Créer un élément temporaire pour analyser le HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      tempDiv.style.fontSize = '14px';
      tempDiv.style.lineHeight = '1.6';
      tempDiv.style.color = '#333';
      document.body.appendChild(tempDiv);
      
      // Fonction récursive pour traiter chaque élément avec ses styles
      const processElement = (element) => {
        const tagName = element.tagName?.toLowerCase();
        const text = element.textContent?.trim();
        
        if (!text && !['ul', 'ol', 'div', 'br'].includes(tagName)) return;
        
        // Appliquer les styles de l'élément
        const elementStyles = applyStyles(element);
        
        if (tagName === 'br') {
          currentY += 5;
          return;
        }
        
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
          checkNewPage(elementStyles.fontSize + 10);
          currentY += 5;
          
          const lines = pdf.splitTextToSize(text, contentWidth);
          lines.forEach(line => {
            checkNewPage(elementStyles.fontSize + 2);
            const xPos = elementStyles.align === 'center' ? pageWidth / 2 : margin;
            const align = elementStyles.align === 'center' ? { align: 'center' } : {};
            pdf.text(line, xPos, currentY, align);
            currentY += elementStyles.fontSize * 0.4 + 2;
          });
          currentY += 5;
        }
        else if (tagName === 'p') {
          checkNewPage(elementStyles.fontSize + 6);
          
          // Traiter le contenu mixte (texte + éléments en gras/italique)
          if (element.children.length > 0) {
            let textParts = [];
            let currentText = '';
            
            // Parser le contenu mixte
            Array.from(element.childNodes).forEach(node => {
              if (node.nodeType === Node.TEXT_NODE) {
                currentText += node.textContent;
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                if (currentText) {
                  textParts.push({ text: currentText, element: element });
                  currentText = '';
                }
                textParts.push({ text: node.textContent, element: node });
              }
            });
            
            if (currentText) {
              textParts.push({ text: currentText, element: element });
            }
            
            // Afficher chaque partie avec son style
            let currentLineY = currentY;
            let currentLineX = margin;
            const lineHeight = elementStyles.fontSize * 0.45 + 2;
            
            textParts.forEach(part => {
              if (part.text.trim()) {
                applyStyles(part.element);
                const words = part.text.split(' ');
                
                words.forEach(word => {
                  if (word.trim()) {
                    const wordWidth = pdf.getTextWidth(word + ' ');
                    
                    if (currentLineX + wordWidth > pageWidth - margin) {
                      currentLineY += lineHeight;
                      currentLineX = margin;
                      checkNewPage(lineHeight);
                    }
                    
                    pdf.text(word, currentLineX, currentLineY);
                    currentLineX += wordWidth;
                  }
                });
              }
            });
            
            currentY = currentLineY + lineHeight + 2;
          } else {
            // Texte simple
            const lines = pdf.splitTextToSize(text, contentWidth);
            lines.forEach(line => {
              checkNewPage(elementStyles.fontSize + 2);
              pdf.text(line, margin, currentY);
              currentY += elementStyles.fontSize * 0.45 + 2;
            });
            currentY += 2;
          }
        }
        else if (tagName === 'ul' || tagName === 'ol') {
          currentY += 2;
          const listItems = element.querySelectorAll('li');
          listItems.forEach((li, index) => {
            applyStyles(li);
            checkNewPage(elementStyles.fontSize + 4);
            
            const bulletPoint = tagName === 'ul' ? '• ' : `${index + 1}. `;
            const liText = li.textContent;
            const liLines = pdf.splitTextToSize(bulletPoint + liText, contentWidth - 10);
            
            liLines.forEach((line, lineIndex) => {
              checkNewPage(elementStyles.fontSize + 2);
              const indent = lineIndex === 0 ? 0 : 10;
              pdf.text(line, margin + indent, currentY);
              currentY += elementStyles.fontSize * 0.45 + 2;
            });
            currentY += 1;
          });
          currentY += 2;
        }
        else if (element.children && element.children.length > 0) {
          // Traiter les éléments enfants
          Array.from(element.children).forEach(child => {
            processElement(child);
          });
        }
        else if (text) {
          // Traiter le texte simple avec les styles appliqués
          checkNewPage(elementStyles.fontSize + 4);
          const lines = pdf.splitTextToSize(text, contentWidth);
          lines.forEach(line => {
            checkNewPage(elementStyles.fontSize + 2);
            pdf.text(line, margin, currentY);
            currentY += elementStyles.fontSize * 0.45 + 2;
          });
          currentY += 2;
        }
      };
      
      // Traiter tous les éléments enfants
      Array.from(tempDiv.children).forEach(child => {
        processElement(child);
      });
      
      // Supprimer l'élément temporaire
      document.body.removeChild(tempDiv);
      
      // Pied de page
      checkNewPage(20);
      currentY = Math.max(currentY + 20, pageHeight - 40);
      pdf.setDrawColor(221, 221, 221);
      pdf.setLineWidth(0.3);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 10;
      
      pdf.setFontSize(9);
      pdf.setTextColor(102, 102, 102);
      pdf.setFont('helvetica', 'normal');
      const footerText = 'Document officiel de l\'OSARC - www.offisport.com';
      pdf.text(footerText, pageWidth / 2, currentY, { align: 'center' });
      
      // Télécharger le PDF
      pdf.save(filename);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };
  
  // Récupération des données du comité directeur depuis Strapi
  const { data: comiteData, loading: comiteLoading, error: comiteError } = useStrapi('comite-directeurs', { 
    populate: '*'
  });
  
  // Récupération des données À Propos depuis Strapi (Single Type)
  const { data: aproposData, loading: aproposLoading, error: aproposError } = useStrapi('a-propos', { populate: '*' });

  // Données par défaut en attendant la création des tables Strapi
  const defaultComiteData = [
    {
      id: 1,
      attributes: {
        NomPrenom: "Philippe Grand",
        Poste: "Président", 
        fonction_depuis: "2015",
        description: "En fonction depuis 2015",
        Photo: null
      }
    },
    {
      id: 2,
      attributes: {
        NomPrenom: "Jean-François Caux",
        Poste: "Vice-président",
        fonction_depuis: "2018", 
        description: "En fonction depuis 2018",
        Photo: null
      }
    },
    {
      id: 3,
      attributes: {
        NomPrenom: "Marie Dumont",
        Poste: "Secrétaire",
        fonction_depuis: "2019",
        description: "En fonction depuis 2019",
        Photo: null
      }
    },
    {
      id: 4,
      attributes: {
        NomPrenom: "Patrick Leroy", 
        Poste: "Trésorier",
        fonction_depuis: "2017",
        description: "En fonction depuis 2017",
        Photo: null
      }
    }
  ];

  const defaultHistorique = `
    <h3>Notre histoire</h3>
    <p>Un Office des sports qui n'est pas municipal – L'Office des Sports de Compiègne (OSC) a été créé le 24 février 1972. A son origine, on trouve Jean Legendre, alors Maire de Compiègne, qui souhaitait créer une structure indépendante de la Mairie, capable de prendre en charge la répartition de la subvention municipale accordée au sport.</p>
    <p>C'est ainsi que l'OSC s'est aussitôt intitulé « Office des sports de Compiègne » et non « Office municipal des sports ». Depuis c'est l'un des rares -si ce n'est l'unique- Office des sports en France à ne pas être municipal.</p>
    <h4>Claude GUIDEZ, le premier</h4>
    <p>C'est Claude Guidez, alors président du Rugby Club Compiégnois, qui prend la tête de l'OSC. Sa première volonté est de mettre en place une politique de transparence. Dès lors, tout club qui ne présente pas une comptabilité claire ne peut recevoir de subvention. Plus globalement, l'objectif du président est de fédérer les clubs sportifs compiégnois.</p>
  `;

  const defaultStatut = `
    <h3>Article 1 - Constitution et dénomination</h3>
    <p>Il est fondé entre les adhérents aux présents statuts une association régie par la loi du 1er juillet 1901, ayant pour titre "Office des Sports de l'Agglomération de la Région de Compiègne" (OSARC).</p>
    <h3>Article 2 - Objet</h3>
    <p>L'association a pour but :</p>
    <ul>
      <li>De promouvoir la pratique des activités physiques et sportives</li>
      <li>De coordonner les actions des associations sportives membres</li>
      <li>De représenter les intérêts collectifs des associations sportives auprès des pouvoirs publics</li>
      <li>De contribuer à l'animation sportive du territoire</li>
    </ul>
  `;

  const defaultReglement = `
    <h3>Titre I - Adhésion</h3>
    <p>Les modalités d'adhésion à l'OSARC sont définies comme suit :</p>
    <ul>
      <li>L'adhésion est ouverte à toute association sportive de l'agglomération de Compiègne</li>
      <li>La demande d'adhésion doit être formulée par écrit au Président de l'OSARC</li>
      <li>L'adhésion est soumise à l'approbation du Comité Directeur</li>
    </ul>
    <h3>Titre II - Fonctionnement</h3>
    <p>Le fonctionnement de l'OSARC s'organise selon les principes suivants :</p>
    <ul>
      <li>L'Assemblée Générale se réunit une fois par an</li>
      <li>Le Comité Directeur se réunit au moins une fois par trimestre</li>
      <li>Le Bureau se réunit mensuellement</li>
    </ul>
  `;

  const defaultCellulesOsarc = `
    <h3>LES CELLULES DE L'OSARC</h3>
    <h4>Cellule finances</h4>
    <ul>
      <li>Martine Lachaussée (Responsable)</li>
      <li>Francis Thoquenne</li>
      <li>Thierry Top</li>
      <li>Jean-Claude Lavernhe</li>
      <li>Michel Ravasio</li>
      <li>Jean-Luc Deresmes</li>
      <li>Jean-François Caux</li>
    </ul>
    <h4>Cellule Event's</h4>
    <ul>
      <li>Jean-Marie Bouché (Responsable)</li>
      <li>Jean-François Caux</li>
      <li>Jean-Luc Deresmes</li>
      <li>Luc Van Vlierberge</li>
      <li>Jean-Paul Marechal</li>
      <li>Michel Ravasio</li>
      <li>Yannick Caudron</li>
    </ul>
    <h4>Cellule marketing et communication</h4>
    <ul>
      <li>Philippe Grand (responsable)</li>
      <li>Jean-François Caux</li>
      <li>Constance Levasseur</li>
      <li>Francis Thoquenne</li>
      <li>Martine Lachaussée</li>
      <li>Luc Van Vlierberge</li>
      <li>Patrick Teixeira</li>
    </ul>
    <h4>Cellule inclus sport</h4>
    <ul>
      <li>Jean-Paul Maréchal (responsable)</li>
      <li>Laurence Lachaud</li>
      <li>Thierry Top</li>
      <li>Luc Van Vlierberge</li>
      <li>Michel Ravasio</li>
      <li>Jean-Marie Bouché</li>
      <li>Constance Levasseur</li>
    </ul>
  `;

  // Utilisation des données Strapi si disponibles, sinon données par défaut
  // Pour les Single Types, les données peuvent être directement dans data ou dans data.attributes
  const strapiAttributes = aproposData?.attributes || aproposData;
  const historiqueContent = strapiAttributes?.Historique || defaultHistorique;
  const statutContent = strapiAttributes?.Statut || defaultStatut;
  const reglementContent = strapiAttributes?.ReglementInterieur || defaultReglement;
  // Ancien champ: cellules_osarc. Nouveau champ attendu côté Strapi: ComiteDirecteur
  const cellulesContent = strapiAttributes?.cellules_osarc || defaultCellulesOsarc;
  const comiteDirecteurContent = strapiAttributes?.ComiteDirecteur || cellulesContent;
  
  // Protection robuste pour les données du comité
  const comiteDataToUse = (comiteData && Array.isArray(comiteData) && comiteData.length > 0) 
    ? comiteData 
    : defaultComiteData;
  
  // Helper pour construire une URL absolue pour les médias Strapi v5
  const getFullMediaUrl = (url) => {
    if (!url) return null;
    
    // Toujours utiliser l'URL de production pour les images
    const base = 'https://api.offisport.com';
    
    try {
      // Normaliser les URLs connues vers la base correcte
      const replaced = url
        .replace(/^http:\/\/localhost:1337/i, base)
        .replace(/^http:\/\/127\.0\.0\.1:1337/i, base)
        .replace(/^http:\/\/192\.168\.[0-9]+\.[0-9]+:1337/i, base);
      if (replaced !== url) return replaced;
      
      // Si c'est déjà une URL absolue et non locale, la retourner
      if (url.startsWith('http') && !url.includes('localhost') && !url.includes('127.0.0.1') && !url.includes('192.168.')) {
        return url;
      }
    } catch (err) {
      console.debug('Erreur parsing URL:', err);
    }
    
    // Concaténer avec la base de production
    return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
  };

  // Extraction robuste de l'URL depuis un champ media Strapi (single)
  const extractPhotoUrl = (photo) => {
    if (!photo) return null;
    try {
      // Strapi v5 le plus courant: { data: { attributes: { url, formats } } } ou tableau
      if (photo.data) {
        const dataObj = Array.isArray(photo.data)
          ? (photo.data[0]?.attributes || photo.data[0])
          : (photo.data.attributes || photo.data);
        const candidate = dataObj?.formats?.thumbnail?.url || dataObj?.formats?.small?.url || dataObj?.url;
        return getFullMediaUrl(candidate);
      }
      // Cas direct (déjà dénormalisé) : { url, formats }
      if (photo.url || photo.formats) {
        const candidate = photo.formats?.thumbnail?.url || photo.formats?.small?.url || photo.url;
        return getFullMediaUrl(candidate);
      }
    } catch (_) {
      // silencieux
    }
    return null;
  };

  // Fallback: initiales si pas d'image
  const getInitials = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return '?';
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase();
  };
  
  // Effet hover + lightbox pour les photos du comité (système simplifié)
  const [hoveredMemberIndex, setHoveredMemberIndex] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageUrl, setLightboxImageUrl] = useState(null);
  
  // Extraire l'URL de l'image large pour la lightbox
  const getLargeImageUrl = (membreData) => {
    if (!membreData.Photo) return null;
    
    try {
      if (membreData.Photo.data) {
        const dataObj = Array.isArray(membreData.Photo.data)
          ? (membreData.Photo.data[0]?.attributes || membreData.Photo.data[0])
          : (membreData.Photo.data.attributes || membreData.Photo.data);
        
        // Priorité: large > medium > thumbnail > url original
        const candidate = dataObj?.formats?.large?.url || 
                         dataObj?.formats?.medium?.url || 
                         dataObj?.formats?.thumbnail?.url || 
                         dataObj?.url;
        
        return getFullMediaUrl(candidate);
      }
      if (membreData.Photo.url || membreData.Photo.formats) {
        const candidate = membreData.Photo.formats?.large?.url || 
                         membreData.Photo.formats?.medium?.url || 
                         membreData.Photo.formats?.thumbnail?.url || 
                         membreData.Photo.url;
        
        return getFullMediaUrl(candidate);
      }
    } catch (err) {
      console.debug('Erreur extraction URL large:', err);
    }
    return null;
  };
  
  const handleImageClick = (membreData) => {
    const largeUrl = getLargeImageUrl(membreData);
    
    // Fallback : utiliser l'URL de la petite image si l'URL large n'est pas trouvée
    const imageUrl = extractPhotoUrl(membreData.Photo);
    
    const finalUrl = largeUrl || imageUrl;
    
    if (finalUrl) {
      setLightboxImageUrl(finalUrl);
      setLightboxOpen(true);
    }
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImageUrl(null);
  };
  

    
  // Logs de debug supprimés
  // Logs de debug supprimés
  
  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } }
  };
  
  const popupVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, delay: 0.1 } }
  };
  
  const contentVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } }
  };
  
const bureauOsarc = "https://www.offisport.com/wp-content/uploads/2024/05/IMG_20240506_194345-1024x768.jpg";


  return (
    <AnimatePresence>
      {isOpen && (
        <PopupOverlay
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <PopupContainer 
            variants={popupVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={e => e.stopPropagation()}
          >
            <PopupHeader>
              <PopupTitle>À propos de l'OSARC</PopupTitle>
              <CloseButton onClick={onClose}>
                <FaTimes />
              </CloseButton>
            </PopupHeader>
            
            <PopupContent>
              <Sidebar>
                <NavItem 
                  $active={activeSection === 'historique'} 
                  onClick={() => setActiveSection('historique')}
                >
                  <div className="icon-container">
                    <FaHistory />
                    <span>Historique</span>
                  </div>
                  <FaChevronRight className="arrow" />
                </NavItem>
                
                <NavItem 
                  $active={activeSection === 'comite'} 
                  onClick={() => setActiveSection('comite')}
                >
                  <div className="icon-container">
                    <FaUsers />
                    <span>Comité directeur</span>
                  </div>
                  <FaChevronRight className="arrow" />
                </NavItem>
                
                <NavItem 
                  $active={activeSection === 'statut'} 
                  onClick={() => setActiveSection('statut')}
                >
                  <div className="icon-container">
                    <FaFileAlt />
                    <span>Statuts</span>
                  </div>
                  <FaChevronRight className="arrow" />
                </NavItem>
                
                <NavItem 
                  $active={activeSection === 'reglement'} 
                  onClick={() => setActiveSection('reglement')}
                >
                  <div className="icon-container">
                    <FaBook />
                    <span>Règlement intérieur</span>
                  </div>
                  <FaChevronRight className="arrow" />
                </NavItem>
              </Sidebar>
              
              <ContentArea>
                <AnimatePresence mode="wait">
                  {activeSection === 'historique' && (
                    <TabContent
                      key="historique"
                      variants={contentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                          <h2>Historique</h2>
                      {aproposLoading ? (
                        <p>Chargement de l'historique...</p>
                      ) : strapiAttributes?.Historique ? (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: processLinksForNewTab(strapiAttributes.Historique)
                          }}
                          style={{
                            lineHeight: '1.6',
                            color: '#333'
                          }}
                        />
                      ) : (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: processLinksForNewTab(historiqueContent)
                          }}
                          style={{
                            lineHeight: '1.6',
                            color: '#333'
                          }}
                        />
                      )}
                    </TabContent>
                  )}
                  
                  {activeSection === 'comite' && (
                    <TabContent
                      key="comite"
                      variants={contentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      <h2>Comité directeur</h2>
                     
                      {comiteLoading ? (
                        <p>Chargement des données du comité...</p>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                          {comiteDataToUse
                            .sort((a, b) => {
                              // Trier par ordre (du plus petit au plus gros)
                              const ordreA = (a?.attributes?.Ordre || a?.Ordre || 999);
                              const ordreB = (b?.attributes?.Ordre || b?.Ordre || 999);
                              return ordreA - ordreB;
                            })
                            .map((membre, index) => {
                            // Logs de debug supprimés
                            
                            // Adapter selon la structure des données (Strapi vs par défaut)
                            const membreData = membre?.attributes ? membre.attributes : membre;
                            
                            if (!membreData) {
                              console.warn('Données de membre vides:', membre);
                              return null;
                            }
                            
                            // Vérifier que le membre a un nom valide
                            if (!membreData.NomPrenom || membreData.NomPrenom.trim() === '') {
                              console.warn('Membre sans nom valide:', membreData);
                              return null;
                            }
                            
                            // Construire l'URL de l'image si elle existe
                            const imageUrl = extractPhotoUrl(membreData.Photo);
                            if (membreData.Photo && !imageUrl) {
                              try { console.warn('Photo détectée mais URL introuvable pour:', membreData.NomPrenom, membreData.Photo); } catch(err) { console.debug('Diagnostic photo (catch):', err); }
                            }
                            
                            // Créer une clé unique stable basée sur l'index et l'ID
                            const uniqueKey = `membre-${index}-${membre.id || 'no-id'}-${membreData.NomPrenom ? membreData.NomPrenom.replace(/\s+/g, '-') : 'unknown'}`;
                            
                            return (
                              <div 
                                key={uniqueKey}
                                style={{ 
                                  background: '#f5f7fa', 
                                  padding: '15px', 
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '15px',
                                  transition: 'box-shadow 0.2s ease',
                                  boxShadow: hoveredMemberIndex === index ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={() => setHoveredMemberIndex(index)}
                                onMouseLeave={() => setHoveredMemberIndex(null)}
                                onClick={() => handleImageClick(membreData)}
                              >
                                {/* Contenu textuel à gauche */}
                                <div style={{ flex: 1 }}>
                                  <h4 style={{ color: '#0066cc', margin: '0 0 8px 0', fontSize: '1rem' }}>
                                    {membreData.Poste || 'Membre'}
                                  </h4>
                                  <p style={{ fontWeight: 'bold', margin: '0 0 5px 0', fontSize: '1.1rem' }}>
                                    {membreData.NomPrenom || 'Nom non défini'}
                                  </p>
                                  {membreData.fonction_depuis && (
                                    <p style={{ color: '#667188', margin: '0 0 5px 0', fontSize: '0.9rem' }}>
                                      En fonction depuis {membreData.fonction_depuis}
                                    </p>
                                  )}
                                  {membreData.description && (
                                    <p style={{ color: '#667188', margin: '0', fontSize: '0.85rem', lineHeight: '1.4' }}>
                                      {membreData.description}
                                    </p>
                                  )}
                                </div>
                                
                                {/* Image à droite */}
                                {imageUrl ? (
                                  <img 
                                    src={imageUrl} 
                                    alt={membreData.NomPrenom || 'Membre du comité'} 
                                    style={{ 
                                      width: '70px', 
                                      height: '70px', 
                                      borderRadius: '50%', 
                                      objectFit: 'cover',
                                      transition: 'transform 0.2s ease',
                                      transform: hoveredMemberIndex === index ? 'scale(1.1)' : 'scale(1)',
                                      cursor: 'pointer',
                                      flexShrink: 0
                                    }} 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleImageClick(membreData);
                                    }}
                                    onError={(e) => {
                                      const current = e.currentTarget;
                                      const src = current.src || '';
                                      // Éviter les boucles infinies
                                      if (!current.dataset.retried && (src.includes('localhost:1337') || src.includes('127.0.0.1:1337') || /192\.168\./.test(src))) {
                                        current.dataset.retried = '1';
                                        const fixed = src
                                          .replace(/^http:\/\/(?:localhost|127\.0\.0\.1)(?::1337)?/i, 'https://api.offisport.com')
                                          .replace(/^http:\/\/192\.168\.[0-9]+\.[0-9]+(?::1337)?/i, 'https://api.offisport.com');
                                        console.warn('Réessai avec URL corrigée:', fixed);
                                        current.src = fixed;
                                        return;
                                      }
                                      console.warn('Échec de chargement de la photo:', src, 'pour', membreData.NomPrenom);
                                    }}
                                  />
                                ) : (
                                  <div 
                                    style={{
                                      width: '70px',
                                      height: '70px',
                                      borderRadius: '50%',
                                      background: '#e7edf6',
                                      color: '#335',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 700,
                                      fontSize: '1.2rem',
                                      flexShrink: 0
                                    }}
                                    title="Photo non disponible"
                                  >
                                    {getInitials(membreData.NomPrenom)}
                                  </div>
                                )}
                              </div>
                            );
                          }).filter(Boolean)}
                        </div>
                      )}
                      
                      {/* Contenu riche "Comité directeur" issu de Strapi (fallback sur ancien contenu cellules) */}
                      {comiteDirecteurContent && (
                        <div style={{ marginTop: '40px' }}>
                          <div 
                            dangerouslySetInnerHTML={{ 
                              __html: processLinksForNewTab(comiteDirecteurContent)
                            }}
                            style={{
                              lineHeight: '1.6',
                              color: '#333'
                            }}
                          />
                        </div>
                      )}

                    </TabContent>
                  )}
                  
                  {activeSection === 'statut' && (
                    <TabContent
                      key="statut"
                      variants={contentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      <h2>Statuts de l'association</h2>
                      {aproposLoading ? (
                        <p>Chargement des statuts...</p>
                      ) : strapiAttributes?.Statut ? (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: processLinksForNewTab(strapiAttributes.Statut)
                          }}
                          style={{
                            lineHeight: '1.6',
                            color: '#333'
                          }}
                        />
                      ) : (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: processLinksForNewTab(statutContent)
                          }}
                          style={{
                            lineHeight: '1.6',
                            color: '#333'
                          }}
                        />
                      )}
                      
                      {/* Bouton de téléchargement PDF - Strapi ou par défaut */}
                      <p style={{ marginTop: '30px' }}>
                        {strapiAttributes?.PDFStatut?.data?.attributes?.url ? (
                          <a 
                            href={strapiAttributes.PDFStatut.data.attributes.url.startsWith('/') 
                              ? `${strapiAttributes.PDFStatut.data.attributes.url}`
                              : strapiAttributes.PDFStatut.data.attributes.url
                            }
                            download
                            style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '10px 15px', 
                              background: '#0066cc', 
                              color: 'white', 
                              borderRadius: '5px', 
                              textDecoration: 'none' 
                            }}
                          >
                            <FaFileAlt /> Télécharger les statuts complets (PDF)
                          </a>
                        ) : (
                          <button
                            onClick={() => {
                              const content = strapiAttributes?.Statut || statutContent;
                              downloadAsPDF(content, 'statuts-osarc.pdf', 'Statuts de l\'OSARC');
                            }}
                            style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '10px 15px', 
                              background: '#0066cc', 
                              color: 'white', 
                              borderRadius: '5px', 
                              border: 'none',
                              cursor: 'pointer',
                              textDecoration: 'none' 
                            }}
                          >
                            <FaFileAlt /> Télécharger les statuts (PDF)
                          </button>
                        )}
                      </p>
                    </TabContent>
                  )}
                  
                  {activeSection === 'reglement' && (
                    <TabContent
                      key="reglement"
                      variants={contentVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      <h2>Règlement intérieur</h2>
                      {aproposLoading ? (
                        <p>Chargement du règlement...</p>
                      ) : strapiAttributes?.ReglementInterieur ? (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: processLinksForNewTab(strapiAttributes.ReglementInterieur)
                          }}
                          style={{
                            lineHeight: '1.6',
                            color: '#333'
                          }}
                        />
                      ) : (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: processLinksForNewTab(reglementContent)
                          }}
                          style={{
                            lineHeight: '1.6',
                            color: '#333'
                          }}
                        />
                      )}
                      
                      {/* Bouton de téléchargement PDF - Strapi ou par défaut */}
                      <p style={{ marginTop: '30px' }}>
                        {strapiAttributes?.PDFReglementInterieur?.data?.attributes?.url ? (
                          <a 
                            href={strapiAttributes.PDFReglementInterieur.data.attributes.url.startsWith('/') 
                              ? `${strapiAttributes.PDFReglementInterieur.data.attributes.url}`
                              : strapiAttributes.PDFReglementInterieur.data.attributes.url
                            }
                            download
                            style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '10px 15px', 
                              background: '#0066cc', 
                              color: 'white', 
                              borderRadius: '5px', 
                              textDecoration: 'none' 
                            }}
                          >
                            <FaBook /> Télécharger le règlement intérieur (PDF)
                          </a>
                        ) : (
                          <button
                            onClick={() => {
                              const content = strapiAttributes?.ReglementInterieur || reglementContent;
                              downloadAsPDF(content, 'reglement-interieur-osarc.pdf', 'Règlement Intérieur de l\'OSARC');
                            }}
                            style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '8px', 
                              padding: '10px 15px', 
                              background: '#0066cc', 
                              color: 'white', 
                              borderRadius: '5px', 
                              border: 'none',
                              cursor: 'pointer',
                              textDecoration: 'none' 
                            }}
                          >
                            <FaBook /> Télécharger le règlement intérieur (PDF)
                          </button>
                        )}
                      </p>
                    </TabContent>
                  )}
                </AnimatePresence>
              </ContentArea>
            </PopupContent>
          </PopupContainer>
        </PopupOverlay>
      )}
      
      {/* Lightbox pour les photos du comité - système simplifié */}
      <ImageLightbox
        isOpen={lightboxOpen}
        imageUrl={lightboxImageUrl}
        onClose={closeLightbox}
      />
    </AnimatePresence>
  );
};

// Composant ImageLightbox simplifié (une seule image)
const ImageLightbox = ({ isOpen, imageUrl, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen || !imageUrl) return null;

  return (
    <LightboxOverlay $isVisible={isOpen} onClick={onClose}>
      <LightboxCloseButton onClick={onClose}>
        ×
      </LightboxCloseButton>
      
      <LightboxImage 
        src={imageUrl} 
        alt="Photo du membre"
        onClick={(e) => e.stopPropagation()}
        onError={(e) => {
          console.error('Erreur chargement image lightbox:', imageUrl);
          e.target.style.display = 'none';
        }}
      />
    </LightboxOverlay>
  );
};

// Styles pour la lightbox (identiques aux actualités)
const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: ${props => props.$isVisible ? 'auto' : 'none'};
`;

const LightboxImage = styled.img`
  max-width: 95vw;
  max-height: 95vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
`;

const LightboxCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #132e57;
  font-size: 28px;
  font-weight: bold;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1000001;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: #4a6bce;
    transform: scale(1.1);
  }
`;



export default AboutPopupSidebar; 